import React, { HTMLAttributes, useState } from "react";
import { Money } from "@/helpers/Money";
import { Cadence } from "../portal/types";
import { AutorenewOutlined, LocalShippingOutlined } from "@mui/icons-material";
import { ProductCard, ProductCardProduct } from "../product/ProductCard";
import { Button } from "@/components/Button";
import { productSelect } from "@/store";
import { cn } from "@/helpers/utils";
import theme from "@/theme";
import { useMediaQuery } from "@mui/material";
import { DroppsTooltip } from "@/components/DroppsTooltip";
import { Dialog } from "@/components/Dialog";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";

export type ButtonAction = {
  displayText: string;
  href?: string;
  action?: () => void;
};

export type OrderLineitemCardProps = {
  product: ProductCardProduct;
  price: Money;
  quantity: number;
  shippingInfo?: Cadence;
  loading?: boolean;
  disabled?: boolean;
  responsive?: boolean;
  isSubBadge?: boolean;
  scentBg?: "white" | "gray";
  orderItemCardActions?: React.ReactNode;
  productAvailable: boolean;
  discountValue?: number | null;
  onSubClick?: () => void;
} & HTMLAttributes<HTMLElement>;

export const OrderLineitemCard = ({
  product,
  price,
  shippingInfo,
  quantity,
  loading,
  disabled,
  responsive,
  scentBg,
  isSubBadge,
  orderItemCardActions,
  productAvailable,
  discountValue,
  onSubClick,
  ...attrs
}: OrderLineitemCardProps) => {
  const isMobile: boolean = useMediaQuery(
    theme.breakpoints.down(theme.breakpoints.values.md)
  );

  const [learnMoreInfoDialogOpen, setLearnMoreInfoDialogOpen] = useState(false);

  const learnMoreInfo = !shippingInfo ? (
    <div className={`${isMobile ? "space-y-4" : "space-y-2 p-2"}`}>
      {!isMobile && (
        <p className="body mb-0 font-semibold uppercase text-primary">
          One-time Purchase
        </p>
      )}
      <p className="body text-sm leading-normal md:leading-tight">
        {discountValue ? (
          <>
            You can{" "}
            <span className="font-bold">
              save {discountValue}% on your next order
            </span>{" "}
            by subscribing to this product. Subscriptions are flexible and can
            be changed at anytime.
          </>
        ) : (
          "You can subscribe to this product. Subscriptions are flexible and can be changed at anytime."
        )}
      </p>
      {productAvailable && (
        <Button
          variant="ghost"
          className="h-auto border-0 p-0"
          size="sm"
          component="button"
          aria-label={`Open create subscription drawer`}
          onClick={onSubClick}
        >
          <span>
            {discountValue
              ? `SUBSCRIBE AND SAVE ${discountValue}%`
              : "Subscribe"}
          </span>
          <ChevronRightIcon className="text-md" />
        </Button>
      )}
    </div>
  ) : (
    <div className={`${isMobile ? "space-y-4" : "space-y-2 p-2"}`}>
      {!isMobile && (
        <p className="body mb-0 font-semibold uppercase text-primary">
          Subscription Purchase
        </p>
      )}
      <p className="body text-sm leading-normal md:leading-tight">
        {discountValue ? (
          <>
            Subscribers{" "}
            <span className="font-bold">
              save {discountValue}% on every order of this product
            </span>{" "}
            and can modify their subscriptions at any time. To see your active
            subscriptions, click below.
          </>
        ) : (
          <>
            Subscribers can modify their subscriptions at any time. To see your
            active subscriptions, click below.
          </>
        )}
      </p>
      <Button
        variant="ghost"
        className="h-auto border-0 p-0"
        size="sm"
        component="a"
        aria-label={`View all subscriptions`}
        href={`/tools/recurring/login`}
      >
        <span>VIEW ALL SUBSCRIPTIONS</span>
        <ChevronRightIcon className="text-md" />
      </Button>
    </div>
  );

  return (
    <div
      className={cn(
        "flex flex-col overflow-hidden rounded-lg bg-primary-50",
        responsive && "sm:flex-row sm:flex-wrap md:flex-nowrap"
      )}
      {...attrs}
    >
      <ProductCard
        className={cn(
          "w-full !bg-transparent",
          scentBg &&
            scentBg === "white" &&
            "[&_.product-card-scent-name]:bg-white",
          responsive && "sm:basis-2/3 md:grow-[2] md:basis-0"
        )}
        productCardValue={{
          ...product,
          size: `${quantity} x ${product.size}`,
        }}
        productTitle={
          <h3
            data-id="product-card-title"
            className={cn("title text-sm", responsive && "lg:text-base")}
          >
            {productSelect.name(product)}
          </h3>
        }
        compact={true}
        hoverable={false}
      />

      <div
        className={cn(
          "flex items-center justify-between space-x-4 px-4 pb-4",
          responsive &&
            "sm:basis-1/3 sm:flex-col sm:items-end sm:justify-center sm:space-x-0 sm:space-y-4 sm:pt-4 md:flex-1 md:flex-row md:items-center md:justify-around md:space-x-4 md:space-y-0"
        )}
      >
        {shippingInfo ? (
          <div>
            {!isMobile ? (
              <DroppsTooltip title={learnMoreInfo}>
                <p className="body flex items-center space-x-1 text-sm">
                  <AutorenewOutlined className="h-4 w-4" />
                  <span className="sr-only">Ships every</span>
                  <span>Subscription</span>
                </p>
              </DroppsTooltip>
            ) : (
              <>
                <p className="body flex items-center space-x-1 text-sm">
                  <AutorenewOutlined className="h-4 w-4" />
                  <span className="sr-only">Ships every</span>
                  <span>Subscription</span>
                </p>
                <Button
                  variant="text"
                  onClick={() => setLearnMoreInfoDialogOpen(true)}
                  className="inline w-full basis-full border-0 text-left text-xs font-normal capitalize tracking-normal"
                  aria-label="Learn about subscription"
                >
                  Learn more <span className="sr-only">about subscription</span>
                </Button>
                <Dialog
                  open={learnMoreInfoDialogOpen}
                  onClose={() => setLearnMoreInfoDialogOpen(false)}
                  title={
                    <span className="text-primary">Subscription Purchase</span>
                  }
                >
                  {learnMoreInfo}
                </Dialog>
              </>
            )}
          </div>
        ) : (
          <div>
            {!isMobile ? (
              productAvailable ? (
                <DroppsTooltip title={learnMoreInfo}>
                  <p className="body flex items-center space-x-1 text-sm">
                    <LocalShippingOutlined className="h-4 w-4" />
                    <span>One-time</span>
                    <span className="sr-only">delivery</span>
                  </p>
                </DroppsTooltip>
              ) : (
                <p className="body flex items-center space-x-1 text-sm">
                  <LocalShippingOutlined className="h-4 w-4" />
                  <span>One-time</span>
                  <span className="sr-only">delivery</span>
                </p>
              )
            ) : (
              <>
                <p className="body flex items-center space-x-1 text-sm">
                  <LocalShippingOutlined className="h-4 w-4" />
                  <span>One-time</span>
                  <span className="sr-only">delivery</span>
                </p>
                {productAvailable && (
                  <Button
                    variant="text"
                    onClick={() => setLearnMoreInfoDialogOpen(true)}
                    className="inline w-full basis-full border-0 text-left text-xs font-normal capitalize tracking-normal"
                    aria-label="Learn about save 25% on subscription"
                  >
                    Learn more{" "}
                    <span className="sr-only">
                      about save 25% on subscription
                    </span>
                  </Button>
                )}
                <Dialog
                  open={learnMoreInfoDialogOpen}
                  onClose={() => setLearnMoreInfoDialogOpen(false)}
                  title={
                    <span className="text-primary">One-time Purchase</span>
                  }
                >
                  {learnMoreInfo}
                </Dialog>
              </>
            )}
          </div>
        )}
        <p
          className={cn(
            "body flex flex-col text-sm",
            responsive && "lg:text-base"
          )}
        >
          <span className="font-semibold">{price.mul(quantity).format()}</span>
          {quantity > 1 && (
            <span className="text-xs">({price.format()}/ea.)</span>
          )}
        </p>
      </div>
      {orderItemCardActions && (
        <div className="sm:basis-full md:flex md:basis-44">
          <hr
            className={cn(
              "divider-x my-0",
              responsive && "md:divider-y md:h-full"
            )}
          />
          <div
            className={cn(
              "flex w-full divide-x divide-y-0 divide-solid divide-primary-200",
              responsive &&
                "md:flex-col md:divide-x-0 md:divide-y md:self-stretch"
            )}
          >
            {orderItemCardActions}
          </div>
        </div>
      )}
    </div>
  );
};
